import { useCallback, useState } from 'react';
import { AnnotationDocument } from './types';

interface HistoryState {
  past: AnnotationDocument[];
  present: AnnotationDocument;
  future: AnnotationDocument[];
}

const MAX_HISTORY = 100;

/** Undo/Redo-Stack fuer das Annotations-Dokument. Jeder `commit` ist ein Schritt. */
export const useAnnotationHistory = (initial: AnnotationDocument) => {
  const [state, setState] = useState<HistoryState>({ past: [], present: initial, future: [] });

  const commit = useCallback((next: AnnotationDocument | ((prev: AnnotationDocument) => AnnotationDocument)) => {
    setState((s) => {
      const resolved = typeof next === 'function' ? next(s.present) : next;
      if (resolved === s.present) return s;
      return {
        past: [...s.past.slice(-(MAX_HISTORY - 1)), s.present],
        present: resolved,
        future: [],
      };
    });
  }, []);

  const undo = useCallback(() => {
    setState((s) => {
      if (s.past.length === 0) return s;
      const previous = s.past[s.past.length - 1];
      return { past: s.past.slice(0, -1), present: previous, future: [s.present, ...s.future] };
    });
  }, []);

  const redo = useCallback(() => {
    setState((s) => {
      if (s.future.length === 0) return s;
      const [next, ...rest] = s.future;
      return { past: [...s.past, s.present], present: next, future: rest };
    });
  }, []);

  const reset = useCallback((doc: AnnotationDocument) => {
    setState({ past: [], present: doc, future: [] });
  }, []);

  return {
    doc: state.present,
    commit,
    undo,
    redo,
    reset,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
};
